import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AgendaOcorrencia, AgendaPrioridade, AgendaRecorrencia } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  agendaDoPeriodo, concluirOcorrencia, excluirTarefa, obterTarefa, perfis, salvarTarefa,
} from '../lib/consultas';
import { hojeISO } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';
import { podeGerenciarEquipe, useSessao } from '../lib/sessao';

// A rotina da equipe, escrita (migração 038). Três escopos numa tela só:
// LOJA (vale para todos), PESSOAL (de uma pessoa) e CONJUNTA (algumas) — a
// diferença é quem participa, e é por isso que o escopo não é um campo do
// formulário: sai de quem você marcou.
//
// A semana é a unidade da tela porque é a unidade da rotina de uma loja
// ("abrir seg-sex às 8h"). O mês inteiro viraria uma grade de 42 células que
// no celular do balcão ninguém lê.

const DIAS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/** Segunda-feira da semana da data (0=domingo no getDay, como no banco). */
function inicioDaSemana(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return hojeISO(d);
}
function somarDias(iso: string, n: number): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return hojeISO(d);
}
function diaCurto(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return `${DIAS[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
const hhmm = (h: string | null) => (h ? h.slice(0, 5) : null);

export default function Agenda() {
  const cliente = useQueryClient();
  const { papel, perfil } = useSessao();
  const gerencia = podeGerenciarEquipe(papel);

  const [semana, setSemana] = useState(() => inicioDaSemana(hojeISO()));
  // De quem é a agenda na tela. O vendedor só vê a dele (a RPC recorta de
  // qualquer jeito); a gerência troca de pessoa para montar a rotina.
  const [deQuem, setDeQuem] = useState<string | null>(null);
  const [editando, setEditando] = useState<string | null | undefined>(undefined);

  const alvo = gerencia ? (deQuem ?? perfil?.id ?? null) : (perfil?.id ?? null);
  const fim = somarDias(semana, 6);

  const equipe = useQuery({
    queryKey: ['perfis'], queryFn: perfis, enabled: gerencia, staleTime: 10 * 60_000,
  });
  const ocorrencias = useQuery({
    queryKey: ['agenda', semana, alvo],
    queryFn: () => agendaDoPeriodo(semana, fim, alvo),
    enabled: Boolean(alvo),
  });

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['agenda'] });

  const concluir = useMutation({
    mutationFn: (p: { o: AgendaOcorrencia; concluir: boolean }) => concluirOcorrencia({
      tarefaId: p.o.tarefa_id, profileId: alvo!, dia: p.o.dia, concluir: p.concluir,
    }),
    onSuccess: recarregar,
  });

  // Agrupa por dia mantendo a ordem que a RPC devolveu (dia, hora, título).
  const porDia = useMemo(() => {
    const mapa = new Map<string, AgendaOcorrencia[]>();
    for (let i = 0; i < 7; i += 1) mapa.set(somarDias(semana, i), []);
    for (const o of ocorrencias.data ?? []) mapa.get(o.dia)?.push(o);
    return [...mapa.entries()];
  }, [ocorrencias.data, semana]);

  const hoje = hojeISO();
  const feitas = (ocorrencias.data ?? []).filter((o) => o.concluida_em).length;
  const total = (ocorrencias.data ?? []).length;

  return (
    <Layout titulo="Agenda">
      <div className="space-y-4">
        <div className="cartao flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2">
            <button type="button" className="botao-secundario px-3 py-1"
                    onClick={() => setSemana(somarDias(semana, -7))}>←</button>
            <button type="button" className="botao-secundario px-3 py-1 text-xs"
                    onClick={() => setSemana(inicioDaSemana(hoje))}>hoje</button>
            <button type="button" className="botao-secundario px-3 py-1"
                    onClick={() => setSemana(somarDias(semana, 7))}>→</button>
            <span className="ml-2 text-sm text-mute">
              {diaCurto(semana)} — {diaCurto(fim)}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {gerencia && (
              <select className="campo py-1 text-sm" value={alvo ?? ''}
                      aria-label="Agenda de quem"
                      onChange={(e) => setDeQuem(e.target.value)}>
                {(equipe.data ?? []).filter((p) => p.ativo).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}{p.id === perfil?.id ? ' (você)' : ''}
                  </option>
                ))}
              </select>
            )}
            <span className="text-xs text-dim">{feitas} de {total} feitas</span>
            <button type="button" className="botao-primario px-3 py-2 text-sm"
                    onClick={() => setEditando(null)}>
              + Tarefa
            </button>
          </div>
        </div>

        {editando !== undefined && (
          <FormularioTarefa
            tarefaId={editando}
            aoFechar={() => setEditando(undefined)}
            aoSalvar={() => { setEditando(undefined); recarregar(); }}
          />
        )}

        {ocorrencias.isPending ? <Carregando /> : null}
        {ocorrencias.error ? <Aviso tipo="erro">{mensagemDeErro(ocorrencias.error)}</Aviso> : null}
        {concluir.error ? <Aviso tipo="erro">{mensagemDeErro(concluir.error)}</Aviso> : null}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {porDia.map(([dia, itens]) => (
            <section key={dia}
                     className={`cartao p-3 ${dia === hoje ? 'border-signal' : ''}`}>
              <p className={`rotulo mb-2 ${dia === hoje ? 'text-signal' : ''}`}>
                {diaCurto(dia)}{dia === hoje ? ' · hoje' : ''}
              </p>
              {itens.length === 0 ? (
                <p className="text-xs text-dim">nada marcado</p>
              ) : (
                <ul className="space-y-2">
                  {itens.map((o) => (
                    <li key={`${o.tarefa_id}-${o.dia}`}
                        className={`border p-2 ${o.concluida_em ? 'border-ok/40 bg-ok/5' : 'border-edge'}`}>
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={Boolean(o.concluida_em)}
                          aria-label={`Concluir ${o.titulo}`}
                          disabled={concluir.isPending}
                          onChange={(e) => concluir.mutate({ o, concluir: e.target.checked })}
                        />
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm ${o.concluida_em ? 'text-dim line-through' : ''}`}>
                            {hhmm(o.hora) ? <span className="font-mono text-xs">{hhmm(o.hora)} </span> : null}
                            {o.titulo}
                          </p>
                          {o.descricao ? <p className="text-xs text-mute">{o.descricao}</p> : null}
                          <p className="mt-1 flex flex-wrap items-center gap-1 text-[0.625rem] uppercase">
                            <span className={`border px-1 ${
                              o.escopo === 'loja' ? 'border-signal/50 text-signal-soft'
                                : o.escopo === 'conjunta' ? 'border-alerta/50 text-alerta' : 'border-edge text-dim'
                            }`}>
                              {o.escopo === 'loja' ? 'da loja' : o.escopo}
                            </span>
                            {o.prioridade === 'alta' && (
                              <span className="border border-erro/50 px-1 text-erro">prioridade</span>
                            )}
                            {o.escopo === 'conjunta' && (
                              <span className="text-dim">
                                {o.participantes.map((p) => p.nome.split(' ')[0]).join(', ')}
                              </span>
                            )}
                          </p>
                        </div>
                        {gerencia && (
                          <button type="button" className="shrink-0 text-xs text-dim underline"
                                  onClick={() => setEditando(o.tarefa_id)}>
                            editar
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </Layout>
  );
}

/**
 * Criar ou editar a REGRA de uma tarefa. Não há campo "escopo": ele sai de
 * quem você marcar — ninguém = a loja inteira, um = pessoal, vários =
 * conjunta. O banco recusa de novo o que esta tela esconder (hierarquia).
 */
function FormularioTarefa({ tarefaId, aoFechar, aoSalvar }: {
  tarefaId: string | null;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const { papel, perfil } = useSessao();
  const gerencia = podeGerenciarEquipe(papel);

  const existente = useQuery({
    queryKey: ['tarefa', tarefaId],
    queryFn: () => obterTarefa(tarefaId!),
    enabled: Boolean(tarefaId),
  });
  const equipe = useQuery({ queryKey: ['perfis'], queryFn: perfis, staleTime: 10 * 60_000 });

  const [f, setF] = useState({
    titulo: '', descricao: '', dataInicio: hojeISO(), dataFim: '', hora: '',
    duracao: '', recorrencia: 'nenhuma' as AgendaRecorrencia,
    diasSemana: [] as number[], diaMes: '', prioridade: 'normal' as AgendaPrioridade,
    participantes: [] as string[],
  });
  const [carregou, setCarregou] = useState(false);

  // Trava de "já carregou": consulta lenta respondendo depois não pode apagar
  // o que a pessoa digitou (a armadilha da folha, no CLAUDE.md).
  if (existente.data && !carregou) {
    const t = existente.data;
    setF({
      titulo: t.titulo, descricao: t.descricao ?? '', dataInicio: t.data_inicio,
      dataFim: t.data_fim ?? '', hora: t.hora ? t.hora.slice(0, 5) : '',
      duracao: t.duracao_min ? String(t.duracao_min) : '',
      recorrencia: t.recorrencia, diasSemana: t.dias_semana ?? [],
      diaMes: t.dia_mes ? String(t.dia_mes) : '', prioridade: t.prioridade,
      participantes: [],
    });
    setCarregou(true);
  }

  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) =>
    setF((a) => ({ ...a, [k]: v }));

  const alternarDia = (d: number) =>
    set('diasSemana', f.diasSemana.includes(d)
      ? f.diasSemana.filter((x) => x !== d) : [...f.diasSemana, d].sort());
  const alternarPessoa = (id: string) =>
    set('participantes', f.participantes.includes(id)
      ? f.participantes.filter((x) => x !== id) : [...f.participantes, id]);

  const salvar = useMutation({
    mutationFn: () => salvarTarefa({
      id: tarefaId,
      titulo: f.titulo,
      descricao: f.descricao || null,
      dataInicio: f.dataInicio,
      dataFim: f.dataFim || null,
      hora: f.hora || null,
      duracaoMin: f.duracao ? Number(f.duracao) : null,
      recorrencia: f.recorrencia,
      diasSemana: f.recorrencia === 'semanal' ? f.diasSemana : [],
      diaMes: f.recorrencia === 'mensal' && f.diaMes ? Number(f.diaMes) : null,
      prioridade: f.prioridade,
      // Vendedor só agenda para si: a tela já entrega ele mesmo, e a RPC
      // confere de novo.
      participantes: gerencia ? f.participantes : [perfil?.id ?? ''],
    }),
    onSuccess: aoSalvar,
  });

  const arquivar = useMutation({
    mutationFn: () => excluirTarefa(tarefaId!),
    onSuccess: aoSalvar,
  });

  const invalido = !f.titulo.trim()
    || (f.recorrencia === 'semanal' && f.diasSemana.length === 0)
    || (f.recorrencia === 'mensal' && !f.diaMes);

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">{tarefaId ? 'Editar tarefa' : 'Nova tarefa'}</h2>

      <div>
        <label className="rotulo" htmlFor="tar-titulo">O que é</label>
        <input id="tar-titulo" className="campo" value={f.titulo} autoFocus
               placeholder="Abrir a loja, conferir o caixa, repor a gôndola…"
               onChange={(e) => set('titulo', e.target.value)} />
      </div>
      <div>
        <label className="rotulo" htmlFor="tar-desc">Detalhe (opcional)</label>
        <input id="tar-desc" className="campo" value={f.descricao}
               onChange={(e) => set('descricao', e.target.value)} />
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div>
          <label className="rotulo" htmlFor="tar-ini">Começa em</label>
          <input id="tar-ini" type="date" className="campo" value={f.dataInicio}
                 onChange={(e) => set('dataInicio', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="tar-fim">Até (opcional)</label>
          <input id="tar-fim" type="date" className="campo" value={f.dataFim}
                 onChange={(e) => set('dataFim', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="tar-hora">Hora (opcional)</label>
          <input id="tar-hora" type="time" className="campo" value={f.hora}
                 onChange={(e) => set('hora', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="tar-dur">Duração (min)</label>
          <input id="tar-dur" className="campo" value={f.duracao} inputMode="numeric"
                 onChange={(e) => set('duracao', e.target.value)} />
        </div>
      </div>

      <div>
        <p className="rotulo">Repete</p>
        <div className="flex flex-wrap gap-2">
          {(['nenhuma', 'diaria', 'semanal', 'mensal'] as AgendaRecorrencia[]).map((r) => (
            <button key={r} type="button" onClick={() => set('recorrencia', r)}
                    aria-pressed={f.recorrencia === r}
                    className={`border px-2 py-1 text-xs ${
                      f.recorrencia === r ? 'border-signal bg-signal/10 font-bold' : 'border-edge text-mute'}`}>
              {r === 'nenhuma' ? 'uma vez' : r}
            </button>
          ))}
        </div>
      </div>

      {f.recorrencia === 'semanal' && (
        <div>
          <p className="rotulo">Em quais dias</p>
          <div className="flex flex-wrap gap-2">
            {DIAS.map((nome, d) => (
              <button key={nome} type="button" onClick={() => alternarDia(d)}
                      aria-pressed={f.diasSemana.includes(d)}
                      className={`border px-2 py-1 text-xs ${
                        f.diasSemana.includes(d) ? 'border-signal bg-signal/10 font-bold' : 'border-edge text-mute'}`}>
                {nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {f.recorrencia === 'mensal' && (
        <div>
          <label className="rotulo" htmlFor="tar-diames">Dia do mês</label>
          <input id="tar-diames" className="campo w-24" value={f.diaMes} inputMode="numeric"
                 onChange={(e) => set('diaMes', e.target.value)} />
          <p className="mt-1 text-xs text-dim">
            Dia 31 cai no último dia dos meses mais curtos — em fevereiro, dia 28.
          </p>
        </div>
      )}

      <div>
        <p className="rotulo">Prioridade</p>
        <div className="flex gap-2">
          {(['baixa', 'normal', 'alta'] as AgendaPrioridade[]).map((p) => (
            <button key={p} type="button" onClick={() => set('prioridade', p)}
                    aria-pressed={f.prioridade === p}
                    className={`border px-2 py-1 text-xs ${
                      f.prioridade === p ? 'border-signal bg-signal/10 font-bold' : 'border-edge text-mute'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {gerencia && (
        <div>
          <p className="rotulo">De quem é</p>
          <p className="mb-2 text-xs text-dim">
            Ninguém marcado = <strong>tarefa da loja</strong>, vale para todos.
            Uma pessoa = a rotina dela. Várias = tarefa em conjunto.
          </p>
          <div className="flex flex-wrap gap-2">
            {(equipe.data ?? []).filter((p) => p.ativo).map((p) => (
              <button key={p.id} type="button" onClick={() => alternarPessoa(p.id)}
                      aria-pressed={f.participantes.includes(p.id)}
                      className={`border px-2 py-1 text-xs ${
                        f.participantes.includes(p.id) ? 'border-signal bg-signal/10 font-bold' : 'border-edge text-mute'}`}>
                {p.nome}
              </button>
            ))}
          </div>
        </div>
      )}

      {tarefaId && (
        <p className="text-xs text-dim">
          Editando a <strong>regra</strong>: a mudança vale para todos os dias em
          que a tarefa aparece, inclusive os já passados desta semana.
        </p>
      )}
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      {arquivar.error ? <Aviso tipo="erro">{mensagemDeErro(arquivar.error)}</Aviso> : null}

      <div className="flex flex-wrap gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>
          Voltar
        </button>
        {tarefaId && (
          <button type="button" className="border border-edge px-3 py-2 text-xs font-bold text-erro hover:border-erro"
                  disabled={arquivar.isPending} onClick={() => arquivar.mutate()}>
            Arquivar
          </button>
        )}
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending || invalido} onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
